/**
 * Dashboard Event Handlers
 *
 * Handles dashboard modal: opening with metrics computation and closing
 *
 * @module eventHandlers/dashboardHandlers
 */

import type { State } from '../types';
import { computeProfitReport, renderProfitDashboardHtml } from '../modules/profitDashboard';
import { exportInventoryCsv } from '../modules/csvExport';

export interface DashboardElements {
  openDashboard?: HTMLElement;
  closeDashboardModal?: HTMLElement;
  dashboardModalBackdrop?: HTMLElement;
  dashboardModalContent?: HTMLElement;
}

/**
 * Setup dashboard event handlers
 *
 * @param els - Dashboard control elements
 * @param state - Application state
 */
export function setupDashboardHandlers(els: DashboardElements, state: State): void {
  // Delegated: the inventory CSV button is re-rendered with the report.
  els.dashboardModalContent?.addEventListener('click', e => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('#export-inventory-csv')) {
      exportInventoryCsv(state);
    }
  });

  if (els.openDashboard) {
    els.openDashboard.addEventListener('click', () => {
      if (els.dashboardModalContent) {
        try {
          const report = computeProfitReport(state.lots, state.settings);
          els.dashboardModalContent.innerHTML = renderProfitDashboardHtml(report);
        } catch (err) {
          els.dashboardModalContent.innerHTML = `<p class="text-danger">Failed to compute metrics: ${String(err)}</p>`;
        }
      }

      // Try global function first, fallback to import
      const windowWithModal = window as Window & { openDashboardModal?: () => void };
      if (typeof windowWithModal.openDashboardModal === 'function') {
        windowWithModal.openDashboardModal();
      } else {
        import('../modules/ui/modals').then(m => m.openDashboardModal());
      }
    });
  }

  if (els.closeDashboardModal) {
    els.closeDashboardModal.addEventListener('click', () => {
      import('../modules/ui/modals').then(m => m.closeDashboardModal());
    });
  }

  if (els.dashboardModalBackdrop) {
    els.dashboardModalBackdrop.addEventListener('click', () => {
      import('../modules/ui/modals').then(m => m.closeDashboardModal());
    });
  }
}
